import { useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    CircularProgress,
    Stack,
    Paper,
    Divider,
} from "@mui/material";
import { safeDateFormat } from "qubCommon/helperMethods/dateHelper";

/**
 * CopyDataDialog
 *
 * Props:
 * - open: boolean → whether the dialog is open
 * - onClose: () => void → close handler
 * - onConfirm: async () => void → confirm action (API call)
 */
const CopyDataDialog = ({ open, onClose, onConfirm, prevApp }) => {

    const [loading, setLoading] = useState(false);

    const handleDecline = async () => {
        try {
            setLoading(true);
            await onConfirm(false); // call API + refetch from parent
        } catch (err) {
            console.error("Error copying data:", err);
        } finally {
            setLoading(false);
            onClose();
        }
    }

    const handleConfirm = async () => {
        try {
            setLoading(true);
            await onConfirm(true); // call API + refetch from parent
        } catch (err) {
            console.error("Error copying data:", err);
        } finally {
            setLoading(false);
            onClose();
        }
    };

    const fields = useMemo(
        () => [
            { label: "Τύπος", value: prevApp?.type ?? "—" },
            { label: "Κατάσταση", value: prevApp?.status ?? "—" },
            { label: "Αριθμός Πρωτοκόλλου", value: prevApp?.protocolNumber ?? "—" },
            { label: "Ημ/νια Υποβολής", value: prevApp?.submittedAt?.dateFormatted },
        ],
        [prevApp]
    );

    return (
        <Dialog
            open={open}
            disableEscapeKeyDown  // ❌ disables closing via Esc
            onClose={(event, reason) => {
                // ❌ prevent closing by clicking outside (backdrop)
                if (reason !== "backdropClick") {
                    onClose();
                }
            }}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle variant='h4'>Ανάκτηση Δεδομένων Αίτησης</DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    {prevApp ? (
                        <>
                            <Typography>Βρέθηκαν δεδομένα από προηγούμενη αίτηση σας στο <strong>Πρόγραμμα 5: Τρίτεκνες/Πολύτεκνες</strong></Typography>

                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack divider={<Divider flexItem />} spacing={1}>
                                    {fields.map((f) => (
                                        <Stack
                                            key={f.label}
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            gap={2}
                                        >
                                            <Typography sx={{}}>{f.label}</Typography>
                                            <Typography sx={{ textAlign: "right", fontWeight: 600 }}>{f.value}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>


                            </Paper>

                            <Typography fontWeight='bold'>Θέλετε να συμπληρωθεί η νέα αίτηση με αυτά τα στοιχεία;</Typography>
                        </>
                    ) : (
                        <>
                            <Typography>Δεν βρέθηκαν διαθέσιμα δεδομένα από προηγούμενη αίτηση.</Typography>
                        </>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button
                    disabled={loading}
                    color="error"
                    variant="contained"
                    onClick={handleDecline}
                >
                    Όχι
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={loading}
                    color="primary"
                    variant="contained"
                >
                    {loading ? (
                        <CircularProgress size={20} sx={{ color: "white" }} />
                    ) : (
                        "Ναι"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CopyDataDialog;
